import { useEffect, useRef } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from 'utils/firebase-config';
import { userIsRoomMember } from 'helpers/utils';
import { toast } from 'react-toastify';
import {
  getPendingJoinRooms,
  addPendingJoinRoom,
  removePendingJoinRoom,
  updatePendingJoinRoomTopic,
  JOIN_REQUEST_SENT_EVENT,
  markJoinRequestDeclined,
  clearJoinRequestDeclined,
  getDeclinedJoinRooms,
} from 'utils/pendingJoinRequests';

const MEMBERSHIP_SYNC_MS = 800;

export type JoinRequestResponseStatus = 'accepted' | 'declined';

export interface JoinRequestResponseDetail {
  roomId: string;
  status: JoinRequestResponseStatus;
  topic: string;
}

export const JOIN_REQUEST_RESPONSE_EVENT = 'join-request-response';

function requestBelongsToUser(req: any, userPk: string | number) {
  const reqPk = req?.user?.pk;
  return reqPk != null && String(reqPk) === String(userPk);
}

function hasPendingRequest(roomRequests: Record<string, any>, userPk: string | number) {
  return Object.values(roomRequests || {}).some((req) =>
    requestBelongsToUser(req, userPk)
  );
}

function dispatchJoinRequestResponse(detail: JoinRequestResponseDetail) {
  window.dispatchEvent(new CustomEvent(JOIN_REQUEST_RESPONSE_EVENT, { detail }));
}

function formatRoomLabel(topic: string) {
  return topic && topic !== 'this room' ? `"${topic}"` : topic;
}

/**
 * Listens per-room for join-request outcomes for the logged-in user.
 * Uses sessionStorage so refresh/reopen after reject still surfaces feedback.
 */
export function useJoinRequestNotifications() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userPk = user?.pk;
  const pendingByRoomRef = useRef<Record<string, { hadPending: boolean; topic: string }>>({});
  const roomsRef = useRef<Record<string, any>>({});
  const notifiedRoomsRef = useRef<Set<string>>(new Set());
  const timersRef = useRef<Record<string, number>>({});
  const roomUnsubsRef = useRef<Record<string, () => void>>({});

  useEffect(() => {
    if (!userPk) {
      return undefined;
    }

    const hydratePendingFromStorage = () => {
      getPendingJoinRooms(userPk).forEach(({ roomId, topic }) => {
        pendingByRoomRef.current[roomId] = { hadPending: true, topic };
      });
    };

    const scheduleOutcomeCheck = (roomId: string) => {
      const state = pendingByRoomRef.current[roomId];
      if (!state?.hadPending || notifiedRoomsRef.current.has(roomId)) {
        return;
      }

      if (timersRef.current[roomId]) {
        window.clearTimeout(timersRef.current[roomId]);
      }

      timersRef.current[roomId] = window.setTimeout(() => {
        delete timersRef.current[roomId];
        if (notifiedRoomsRef.current.has(roomId)) {
          return;
        }

        const room = roomsRef.current[roomId];
        const joined = userIsRoomMember(room, userPk);
        const topic =
          room?.topic || pendingByRoomRef.current[roomId]?.topic || 'this room';
        const displayLabel = formatRoomLabel(topic);

        if (joined) {
          toast.success(`You've been accepted to join ${displayLabel}.`);
          clearJoinRequestDeclined(userPk, roomId);
          dispatchJoinRequestResponse({ roomId, status: 'accepted', topic });
        } else {
          toast.warning(
            `Your request to join ${displayLabel} was declined by the host.`
          );
          markJoinRequestDeclined(userPk, roomId);
          dispatchJoinRequestResponse({ roomId, status: 'declined', topic });
        }

        notifiedRoomsRef.current.add(roomId);
        removePendingJoinRoom(userPk, roomId);
        pendingByRoomRef.current[roomId] = { hadPending: false, topic };
      }, MEMBERSHIP_SYNC_MS);
    };

    const handleRoomRequests = (roomId: string, roomRequests: Record<string, any>) => {
      const hasPending = hasPendingRequest(roomRequests, userPk);
      const storedPending = getPendingJoinRooms(userPk).some((r) => r.roomId === roomId);
      const state = pendingByRoomRef.current[roomId];
      const roomData = roomsRef.current[roomId];
      const topic = roomData?.topic || state?.topic || 'this room';

      if (roomData?.topic) {
        updatePendingJoinRoomTopic(userPk, roomId, roomData.topic);
        if (pendingByRoomRef.current[roomId]) {
          pendingByRoomRef.current[roomId].topic = roomData.topic;
        }
      }

      if (hasPending) {
        pendingByRoomRef.current[roomId] = { hadPending: true, topic };
        if (!storedPending) {
          addPendingJoinRoom(userPk, roomId, topic);
        }
        notifiedRoomsRef.current.delete(roomId);
        if (timersRef.current[roomId]) {
          window.clearTimeout(timersRef.current[roomId]);
          delete timersRef.current[roomId];
        }
        return;
      }

      const hadPending = state?.hadPending || storedPending;
      if (!hadPending) {
        return;
      }

      if (!pendingByRoomRef.current[roomId]) {
        pendingByRoomRef.current[roomId] = { hadPending: true, topic };
      }

      scheduleOutcomeCheck(roomId);
    };

    const attachRoomListener = (roomId: string) => {
      if (roomUnsubsRef.current[roomId]) {
        return;
      }

      const requestsRef = ref(db, `requests/${roomId}/requests`);
      roomUnsubsRef.current[roomId] = onValue(requestsRef, (snapshot) => {
        handleRoomRequests(roomId, snapshot.val() || {});
      });
    };

    const detachRoomListener = (roomId: string) => {
      if (roomUnsubsRef.current[roomId]) {
        roomUnsubsRef.current[roomId]();
        delete roomUnsubsRef.current[roomId];
      }
    };

    hydratePendingFromStorage();
    const roomsToWatch = new Set<string>([
      ...getPendingJoinRooms(userPk).map(({ roomId }) => roomId),
      ...getDeclinedJoinRooms(userPk),
    ]);
    roomsToWatch.forEach((roomId) => attachRoomListener(roomId));

    const roomsListener = ref(db, 'rooms');
    const unsubRooms = onValue(roomsListener, (snap) => {
      roomsRef.current = snap.val() || {};
    });

    const onJoinRequestSent = (event: Event) => {
      const { roomId, topic } = (event as CustomEvent<{ roomId: string; topic: string }>)
        .detail;
      pendingByRoomRef.current[roomId] = { hadPending: true, topic };
      notifiedRoomsRef.current.delete(roomId);
      attachRoomListener(roomId);
    };

    window.addEventListener(JOIN_REQUEST_SENT_EVENT, onJoinRequestSent);

    return () => {
      unsubRooms();
      window.removeEventListener(JOIN_REQUEST_SENT_EVENT, onJoinRequestSent);
      Object.keys(roomUnsubsRef.current).forEach(detachRoomListener);
      Object.values(timersRef.current).forEach((timerId) => window.clearTimeout(timerId));
      timersRef.current = {};
    };
  }, [userPk]);
}
